import { FunctionComponent } from 'react'
import styles from './mealHandler.module.scss'
import { useTranslation } from 'react-i18next'
import { MealChildWithoutMenu, MealDayDetailByType, MealDetail, MealTypeDetail } from '@/types/Meal'
import Indicator from '@/components/_custom/Indicator/indicator'
import Desktop from '@/modules/desktopMobile/desktop.tsx'

interface MealIndicatorsProps {
    mealDayDetailByType: MealDayDetailByType | null
    getOrderAdjustment: (detail: MealDetail, contextKey?: string) => number
    onScrollToChildrenWithoutMenu?: () => void
}

const MealIndicators: FunctionComponent<MealIndicatorsProps> = ({
    mealDayDetailByType,
    getOrderAdjustment,
    onScrollToChildrenWithoutMenu
}: MealIndicatorsProps) => {
    const { t } = useTranslation()

    const mealFrozen: boolean =
        (mealDayDetailByType?.typeDetails.length ?? 0) > 0 &&
        (mealDayDetailByType?.typeDetails.every((td: MealTypeDetail): boolean => td.frozen) ?? false)

    const totalMeals: number = mealDayDetailByType
        ? mealDayDetailByType.typeDetails.reduce((acc: number, td: MealTypeDetail): number => {
              return (
                  acc +
                  td.details.reduce((a: number, d: MealDetail): number => {
                      if (td.frozen) return a + d.quantityReserved.length + d.quantityAdded
                      const contextKey: string = `${td.type}_${td.sectionId}`
                      return a + d.quantityReserved.length + getOrderAdjustment(d, contextKey)
                  }, 0)
              )
          }, 0)
        : 0

    const childrenWithoutMenuCount: number = mealDayDetailByType
        ? new Set(
              mealDayDetailByType.typeDetails
                  .flatMap((td: MealTypeDetail): MealChildWithoutMenu[] => td.childsWithoutMenu)
                  .map((c: MealChildWithoutMenu): number => c.childId)
          ).size
        : 0

    const totalReal: number = mealDayDetailByType
        ? mealDayDetailByType.typeDetails.reduce(
              (acc: number, td: MealTypeDetail): number =>
                  acc + td.details.reduce((a: number, d: MealDetail): number => a + d.quantityReal.length, 0),
              0
          )
        : 0

    if (totalMeals <= 0 && childrenWithoutMenuCount <= 0) return null

    return (
        <div className={styles.IndicatorsContainer}>
            <Desktop>
                {childrenWithoutMenuCount > 0 && (
                    <div
                        onClick={onScrollToChildrenWithoutMenu}
                        style={onScrollToChildrenWithoutMenu ? { cursor: 'pointer' } : undefined}
                    >
                        <Indicator
                            count={childrenWithoutMenuCount}
                            backgroundWhite
                            badge={'warning'}
                            text={childrenWithoutMenuCount > 1 ? t('childsWithoutMenu') : t('childWithoutMenu')}
                        />
                    </div>
                )}
            </Desktop>
            <Indicator count={totalMeals} backgroundWhite text={mealFrozen ? t('ordered') : t('toOrder')} />
            <Indicator count={totalReal} backgroundWhite text={mealFrozen ? t('real') : t('reserved')} />
        </div>
    )
}

export default MealIndicators
